export const IDENTITY_WORKBENCH_PANELS = [
  "gallery",
  "comparison",
  "matrix",
  "timeline",
  "history",
  "downstream",
] as const;

export type IdentityWorkbenchPanel = (typeof IDENTITY_WORKBENCH_PANELS)[number];

export type IdentityWorkbenchState = {
  open: boolean;
  identityId: string | null;
  manifestVersion: number | null;
  selectedReportArtifactId: string | null;
  selectedInstanceId: string | null;
  timeUs: number;
  panel: IdentityWorkbenchPanel;
  refreshEpoch: number;
};

export const INITIAL_IDENTITY_WORKBENCH_STATE: IdentityWorkbenchState = {
  open: false,
  identityId: null,
  manifestVersion: null,
  selectedReportArtifactId: null,
  selectedInstanceId: null,
  timeUs: 0,
  panel: "gallery",
  refreshEpoch: 0,
};

export type IdentityWorkbenchAction =
  | { type: "open"; identityId: string; manifestVersion?: number | null }
  | { type: "close" }
  | { type: "select_identity"; identityId: string }
  | { type: "select_manifest"; manifestVersion: number }
  | { type: "select_report"; artifactId: string | null }
  | { type: "select_instance"; instanceId: string | null }
  | { type: "set_time"; timeUs: number }
  | { type: "set_panel"; panel: IdentityWorkbenchPanel }
  | { type: "evidence_refreshed" };

export function identityWorkbenchReducer(
  state: IdentityWorkbenchState,
  action: IdentityWorkbenchAction,
): IdentityWorkbenchState {
  switch (action.type) {
    case "open":
      return {
        ...INITIAL_IDENTITY_WORKBENCH_STATE,
        open: true,
        identityId: action.identityId,
        manifestVersion: action.manifestVersion ?? null,
        refreshEpoch: state.refreshEpoch,
      };
    case "close":
      return { ...state, open: false };
    case "select_identity":
      if (action.identityId === state.identityId) return state;
      return {
        ...state,
        identityId: action.identityId,
        selectedReportArtifactId: null,
        selectedInstanceId: null,
        timeUs: 0,
      };
    case "select_manifest":
      if (action.manifestVersion === state.manifestVersion) return state;
      return {
        ...state,
        manifestVersion: action.manifestVersion,
        identityId: null,
        selectedReportArtifactId: null,
        selectedInstanceId: null,
        timeUs: 0,
      };
    case "select_report":
      return { ...state, selectedReportArtifactId: action.artifactId };
    case "select_instance":
      return { ...state, selectedInstanceId: action.instanceId };
    case "set_time":
      return {
        ...state,
        timeUs:
          Number.isSafeInteger(action.timeUs) && action.timeUs >= 0
            ? action.timeUs
            : state.timeUs,
      };
    case "set_panel":
      return { ...state, panel: action.panel };
    case "evidence_refreshed":
      // All investigative selection survives a one-time signed-artifact refresh.
      return { ...state, refreshEpoch: state.refreshEpoch + 1 };
    default:
      return state;
  }
}

export type IdentityDeepLinkHints = {
  identityId: string | null;
  manifestVersion: number | null;
  instanceId: string | null;
  timeUs: number | null;
  panel: IdentityWorkbenchPanel | null;
};

const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,199}$/;

function safeId(value: string | null) {
  return value != null && SAFE_ID.test(value) ? value : null;
}

export function parseIdentityDeepLinkHints(
  search: string,
): IdentityDeepLinkHints {
  const params = new URLSearchParams(search);
  const rawVersion = Number(params.get("identityManifest"));
  const rawTime = Number(params.get("identityTimeUs"));
  const rawPanel = params.get("identityPanel");
  return {
    identityId: safeId(params.get("identity")),
    manifestVersion:
      Number.isSafeInteger(rawVersion) && rawVersion > 0 ? rawVersion : null,
    instanceId: safeId(params.get("identityInstance")),
    timeUs: Number.isSafeInteger(rawTime) && rawTime >= 0 ? rawTime : null,
    panel: (IDENTITY_WORKBENCH_PANELS as readonly string[]).includes(
      rawPanel ?? "",
    )
      ? (rawPanel as IdentityWorkbenchPanel)
      : null,
  };
}
